/**
 * W-31d step 3 / step 4 — the shapes `validate_import`, `post_import` and
 * `revert_import` return, and the counting the preview does on top of them.
 *
 * The SQL result is authoritative (spec §8.5): nothing here re-decides whether
 * a row is an error. These helpers only group, filter and total what the server
 * already said, and translate the message *code* into a label — the message text
 * itself stays the server's, because it carries the value the owner has to fix.
 */
import { D, type Dec } from '../money';
import { searchKey } from '../text';
import type { ImportRowStatus } from '../constants';

export type ImportMessageLevel = 'error' | 'warning' | 'note';

export interface ImportMessage {
  code: string;
  level: ImportMessageLevel;
  field: string | null;
  message: string;
}

/**
 * Every code `private.import_msg` can emit (migration 20260923000005). The list
 * is what `imports.codes.*` must cover in both languages — a test walks it.
 */
export const IMPORT_MESSAGE_CODES = [
  // items
  'sku_required',
  'sku_format',
  'sku_duplicate',
  'name_required',
  'type_required',
  'type_unknown',
  'base_unit_default',
  'barcode_check_digit',
  'already_exists',
  'lot_tracked_locked',
  // opening stock
  'item_unknown',
  'item_not_stocked',
  'item_draft',
  'name_mismatch',
  'warehouse_unknown',
  'fba_forbidden',
  'location_warehouse',
  'location_unknown',
  'row_duplicate',
  'qty_required',
  'qty_positive',
  'qty_not_integral',
  'unit_unknown',
  'unit_default',
  'lot_not_allowed',
  'lot_item_mismatch',
  'lot_required',
  'currency_unknown',
  'fx_missing',
  'cost_missing',
  'opening_decrease',
  'opening_locked',
  'unchanged',
] as const;

export type ImportMessageCode = (typeof IMPORT_MESSAGE_CODES)[number];

/** `import_rows.parsed` for `opening_stock` — what the poster will actually write. */
export interface OpeningStockParsed {
  item_id?: string;
  sku?: string;
  warehouse_id?: string;
  location_id?: string;
  lot_id?: string;
  lot_no?: string;
  qty?: number;
  unit?: string;
  unit_factor?: number;
  qty_base?: number;
  unit_cost?: number;
  currency?: string;
  received_at?: string;
  expiry_date?: string;
  supplier?: string;
  note?: string;
}

/** `import_rows.snapshot`: the balance the row was measured against. */
export interface ImportRowSnapshot {
  expected_qty?: number | null;
  variance?: number | null;
  /** items only — whether the row created the item (revert reads it) */
  created?: boolean;
}

export interface ImportRowResult {
  row_no: number;
  status: ImportRowStatus;
  messages: ImportMessage[];
  parsed: OpeningStockParsed | null;
  snapshot: ImportRowSnapshot | null;
  /** the file cells, from `import_rows.raw` or kept client-side after staging */
  raw?: Record<string, string> | null;
  target_id?: string | null;
  /**
   * "Hareket no" of step 4: the movement the poster wrote for this row, joined
   * from `stock_movements` after `post_import` (the count line in `target_id` is
   * what carries it). Null for a row that wrote nothing — skipped, unchanged, or
   * a row whose quantity already matched.
   */
  movement_no?: string | null;
}

export interface ValidateResult {
  batch_id: string;
  status: string;
  row_count: number;
  ok_count: number;
  warning_count: number;
  error_count: number;
  skipped_count: number;
  unchanged_count: number;
  rows: ImportRowResult[];
}

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0) || 0);

export function parseImportMessages(value: unknown): ImportMessage[] {
  if (!Array.isArray(value)) return [];
  return value.map((m) => {
    const o = obj(m);
    const level = o.level === 'error' || o.level === 'warning' ? o.level : 'note';
    return {
      code: String(o.code ?? 'unknown'),
      level,
      field: o.field == null ? null : String(o.field),
      message: String(o.message ?? ''),
    };
  });
}

export function parseValidateResult(value: unknown): ValidateResult {
  const o = obj(value);
  const rows = Array.isArray(o.rows) ? o.rows : [];
  return {
    batch_id: String(o.batch_id ?? ''),
    status: String(o.status ?? 'validated'),
    row_count: num(o.row_count),
    ok_count: num(o.ok_count),
    warning_count: num(o.warning_count),
    error_count: num(o.error_count),
    skipped_count: num(o.skipped_count),
    unchanged_count: num(o.unchanged_count),
    rows: rows.map((r) => {
      const row = obj(r);
      return {
        row_no: num(row.row_no),
        status: (row.status as ImportRowStatus) ?? 'ok',
        messages: parseImportMessages(row.messages),
        parsed: row.parsed ? (row.parsed as OpeningStockParsed) : null,
        snapshot: row.snapshot ? (row.snapshot as ImportRowSnapshot) : null,
      };
    }),
  };
}

/** `post_opening_stock` / `post_items` summary — whichever keys the kind fills. */
export interface ImportSummary {
  movements?: number;
  /**
   * `stock_movements.batch_id` of the posting — every movement this import wrote
   * carries it, which is how step 4 reads the movement numbers back without
   * asking for 2.000 row ids by hand.
   */
  batch_id?: string;
  first_no?: string | null;
  last_no?: string | null;
  count_nos?: string[];
  lots_created?: number;
  value_in_base?: number;
  value_out_base?: number;
  skipped?: number;
  created?: number;
  updated?: number;
  reverted_movements?: number;
  reverted_items?: number;
}

export function parseImportSummary(value: unknown): ImportSummary {
  const o = obj(value);
  const nos = Array.isArray(o.count_nos) ? o.count_nos.map((n) => String(n)) : undefined;
  return { ...(o as ImportSummary), count_nos: nos };
}

// ── the preview's own arithmetic ─────────────────────────────────────────────
export const IMPORT_ROW_FILTERS = ['all', 'ok', 'warning', 'error', 'unchanged'] as const;
export type ImportRowFilter = (typeof IMPORT_ROW_FILTERS)[number];

export function matchesRowFilter(row: ImportRowResult, filter: ImportRowFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'ok') return row.status === 'ok';
  if (filter === 'unchanged') return row.status === 'unchanged' || row.status === 'skipped';
  return row.status === filter;
}

/** "SKU, lot, lokasyon ara…" — the file cells plus what the server resolved. */
export function rowSearchFields(row: ImportRowResult): (string | null | undefined)[] {
  const raw = row.raw ?? {};
  return [
    raw.sku,
    raw.item_name,
    raw.warehouse_code,
    raw.location_code,
    raw.lot_no,
    row.parsed?.sku,
    row.parsed?.lot_no,
    String(row.row_no),
  ];
}

export function filterImportRows(rows: ImportRowResult[], filter: ImportRowFilter, term = ''): ImportRowResult[] {
  const q = term.trim() ? searchKey(term.trim()) : '';
  return rows.filter((r) => {
    if (!matchesRowFilter(r, filter)) return false;
    if (!q) return true;
    return rowSearchFields(r).some((f) => f && searchKey(String(f)).includes(q));
  });
}

/**
 * The two company facts the row list cannot read out of `validate_import`, and
 * without which the screen would understate what the posting does:
 *
 *   * `standardCost` — `item_costs.standard_cost_base` per base unit. A row
 *     whose file gives no unit cost is valued from it by
 *     `private.count_line_cost`, so leaving it out drew "—" next to a row that
 *     books real money.
 *   * `lotTracked` — the items with lot tracking on. A lot-tracked row with an
 *     empty "Lot no" gets a generated one (DECISIONS 23 Sep), which is a new lot
 *     the file never names.
 *
 * Both are optional: without them the helpers behave exactly as they did with
 * the file alone, so nothing here ever invents a figure.
 */
export interface ImportValueContext {
  /** item id → `item_costs.standard_cost_base` (per base unit, company base currency) */
  standardCost?: ReadonlyMap<string, number>;
  /** the company's base currency — the currency `standard_cost_base` is kept in */
  baseCurrency?: string;
  /** item ids whose rows get a generated lot number when the lot cell is empty */
  lotTracked?: ReadonlySet<string>;
}

/** A row that writes and will be given a generated lot number (§8.1, 23 Sep). */
export function willGenerateLot(row: ImportRowResult, ctx: ImportValueContext = {}): boolean {
  if (row.status !== 'ok' && row.status !== 'warning') return false;
  const p = row.parsed;
  if (!p?.item_id || p.lot_id || p.lot_no) return false;
  return !!ctx.lotTracked?.has(p.item_id);
}

export interface ImportRowCounts {
  total: number;
  ok: number;
  warning: number;
  error: number;
  unchanged: number;
  /** rows that will produce a write: everything but error / unchanged / skipped */
  postable: number;
  /**
   * New lots: a row naming a lot the company does not have yet, plus — when the
   * context knows which items are lot-tracked — the rows whose lot number the
   * poster generates. With that context this matches the `lots_created` the
   * result banner reports; without it, it stays the file's own lower bound.
   */
  newLots: number;
}

export function countImportRows(rows: ImportRowResult[], ctx: ImportValueContext = {}): ImportRowCounts {
  const counts: ImportRowCounts = { total: rows.length, ok: 0, warning: 0, error: 0, unchanged: 0, postable: 0, newLots: 0 };
  for (const r of rows) {
    if (r.status === 'ok') counts.ok += 1;
    else if (r.status === 'warning') counts.warning += 1;
    else if (r.status === 'error') counts.error += 1;
    else counts.unchanged += 1;
    if (r.status === 'ok' || r.status === 'warning') {
      counts.postable += 1;
      if ((r.parsed?.lot_no && !r.parsed.lot_id) || willGenerateLot(r, ctx)) counts.newLots += 1;
    }
  }
  return counts;
}

export interface ImportRowValue {
  amount: Dec;
  currency: string;
  /** the unit cost behind the figure, in `currency` per the row's own unit */
  unitCost: Dec;
  /** where the cost came from — the two notes of the drawn "Değer" cell */
  source: 'file' | 'standard';
}

/**
 * Value of one row, the way the poster will value it:
 *
 *   * the file's own cost when it gives one — quantity × unit cost in the row's
 *     currency ("1.561,60 ₺/koli · dosya");
 *   * otherwise the item's `standard_cost_base` × the base quantity, in the
 *     company's base currency ("912,60 ₺/koli · standart"), which is what
 *     `private.count_line_cost` books.
 *
 * Null when neither exists: such a row is written at 0 ₺ (the `cost_missing`
 * warning says so) and no figure is invented for it here.
 */
export function rowValue(row: ImportRowResult, ctx: ImportValueContext = {}): ImportRowValue | null {
  const p = row.parsed;
  const qty = p?.qty;
  if (qty == null) return null;
  if (p?.unit_cost != null) {
    return { amount: D(qty).times(p.unit_cost), currency: p.currency ?? 'TRY', unitCost: D(p.unit_cost), source: 'file' };
  }
  const standard = p?.item_id ? ctx.standardCost?.get(p.item_id) : undefined;
  if (standard == null) return null;
  // `standard_cost_base` is per base unit; the cell shows it per the row's unit
  const factor = p?.unit_factor != null && p.unit_factor !== 0 ? D(p.unit_factor) : D(1);
  const base = p?.qty_base != null ? D(p.qty_base) : D(qty).times(factor);
  return {
    amount: base.times(standard),
    currency: ctx.baseCurrency ?? 'TRY',
    unitCost: D(standard).times(factor),
    source: 'standard',
  };
}

/**
 * Totals per currency — a file may price one row in USD, and a single sum would
 * silently add dollars to lira (the fix commit 01f8cf1 made for the Cariler
 * balance column). `unpriced` counts the rows left out.
 */
export interface ImportValueTotals {
  byCurrency: { currency: string; amount: Dec }[];
  unpriced: number;
}

export function totalImportValue(rows: ImportRowResult[], ctx: ImportValueContext = {}): ImportValueTotals {
  const map = new Map<string, Dec>();
  let unpriced = 0;
  for (const r of rows) {
    if (r.status === 'error' || r.status === 'unchanged' || r.status === 'skipped') continue;
    const v = rowValue(r, ctx);
    if (!v) {
      unpriced += 1;
      continue;
    }
    map.set(v.currency, (map.get(v.currency) ?? D(0)).plus(v.amount));
  }
  return { byCurrency: [...map.entries()].map(([currency, amount]) => ({ currency, amount })), unpriced };
}

/** The confirmation sheet groups by warehouse code, in first-seen file order. */
export interface WarehouseGroup {
  code: string;
  rows: number;
  newLots: number;
  value: ImportValueTotals;
}

export function groupByWarehouse(rows: ImportRowResult[], ctx: ImportValueContext = {}): WarehouseGroup[] {
  const order: string[] = [];
  const buckets = new Map<string, ImportRowResult[]>();
  for (const r of rows) {
    if (r.status === 'error' || r.status === 'unchanged' || r.status === 'skipped') continue;
    const code = (r.raw?.warehouse_code ?? '').trim() || '—';
    if (!buckets.has(code)) {
      buckets.set(code, []);
      order.push(code);
    }
    buckets.get(code)!.push(r);
  }
  return order.map((code) => {
    const group = buckets.get(code)!;
    return { code, rows: group.length, newLots: countImportRows(group, ctx).newLots, value: totalImportValue(group, ctx) };
  });
}

/**
 * Rows whose quantity is below the stock that is already there. The database
 * refuses every one of them (`opening_decrease` is an error, and
 * `post_opening_stock` guards the case again): the import never writes a
 * decrease, so this is the count of rows the owner has to fix with a count —
 * not, as the artboard draws it, a count of decreases about to be posted
 * (DECISIONS, 24 Sep).
 */
export function countDecreases(rows: ImportRowResult[]): number {
  return rows.filter((r) => r.messages.some((m) => m.code === 'opening_decrease')).length;
}

/** "… satır 0 ₺ ile değerlenecek (maliyet yok)" */
export function countWithoutCost(rows: ImportRowResult[]): number {
  return rows.filter((r) => r.status !== 'error' && r.messages.some((m) => m.code === 'cost_missing')).length;
}

/**
 * The "Hata" cell of the failing-rows file: every error and warning of the row
 * in the order the preview stacked them. Notes are left out — they explain what
 * the import decided, not what the owner has to change.
 */
export function errorRowMessage(row: ImportRowResult): string {
  return row.messages
    .filter((m) => m.level === 'error' || m.level === 'warning')
    .map((m) => m.message)
    .join(' · ');
}
